/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `memo_glob` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class MemoGlobMesg : Mesg {
    public constructor() : super("MemoGlob", Profile.MesgNum.MEMO_GLOB)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** part_index. Sequence number of memo blocks */
    public var partIndex: UInt?
        get() = getFieldValue(PART_INDEX_FIELD_NUM) as? UInt
        set(value) = setFieldValue(PART_INDEX_FIELD_NUM, value)

    /** Number of values stored in memo. */
    public val numMemo: Int
        get() = getField(MEMO_FIELD_NUM)?.numValues ?: 0

    /** memo. Deprecated. Use data field. */
    public var memo: List<UByte?>?
        get() {
            val field = getField(MEMO_FIELD_NUM) ?: return null
            return List(field.numValues) { getMemo(it) }
        }
        set(value) {
            removeField(MEMO_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMemo(index, element) }
        }

    public fun getMemo(index: Int): UByte? =
        getFieldValue(MEMO_FIELD_NUM, index) as? UByte

    public fun setMemo(index: Int, value: UByte?) {
        setFieldValue(MEMO_FIELD_NUM, value, index)
    }

    /** mesg_num. Message Number of the parent message */
    public var mesgNum: MesgNum?
        get() = (getFieldValue(MESG_NUM_FIELD_NUM) as? UShort)?.let { MesgNum.fromValue(it) }
        set(value) = setFieldValue(MESG_NUM_FIELD_NUM, value?.value)

    /** parent_index. Index of mesg that this glob is associated with. */
    public var parentIndex: UShort?
        get() = getFieldValue(PARENT_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(PARENT_INDEX_FIELD_NUM, value)

    /** field_num. Field within the parent that this glob is associated with */
    public var fieldNum: UByte?
        get() = getFieldValue(FIELD_NUM_FIELD_NUM) as? UByte
        set(value) = setFieldValue(FIELD_NUM_FIELD_NUM, value)

    /** Number of values stored in data. */
    public val numData: Int
        get() = getField(DATA_FIELD_NUM)?.numValues ?: 0

    /** data. Block of utf8 bytes. Note, mutltibyte characters may be split across adjoining memo_glob messages. */
    public var data: List<UByte?>?
        get() {
            val field = getField(DATA_FIELD_NUM) ?: return null
            return List(field.numValues) { getData(it) }
        }
        set(value) {
            removeField(DATA_FIELD_NUM)
            value?.forEachIndexed { index, element -> setData(index, element) }
        }

    public fun getData(index: Int): UByte? =
        getFieldValue(DATA_FIELD_NUM, index) as? UByte

    public fun setData(index: Int, value: UByte?) {
        setFieldValue(DATA_FIELD_NUM, value, index)
    }

    public companion object {
        public const val PART_INDEX_FIELD_NUM: UByte = 250u
        public const val MEMO_FIELD_NUM: UByte = 0u
        public const val MESG_NUM_FIELD_NUM: UByte = 1u
        public const val PARENT_INDEX_FIELD_NUM: UByte = 2u
        public const val FIELD_NUM_FIELD_NUM: UByte = 3u
        public const val DATA_FIELD_NUM: UByte = 4u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[250] = Field("PartIndex", 250u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[0] = Field("Memo", 0u, BaseType.BYTE, 1.0, 0.0, "", false)
            fields[1] = Field("MesgNum", 1u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[2] = Field("ParentIndex", 2u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[3] = Field("FieldNum", 3u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[4] = Field("Data", 4u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            return fields
        }
    }
}
